import { Injectable } from '@angular/core';
import { from, map, Observable, reduce, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { DocumentServiceSpa } from 'src/app/generated/services/documents/documentSpa';
import { Document as DocumentInternal, FileChunkResponse } from 'src/app/generated/services/documents/document';

export interface DocumentReference {
  id: string;
  fileId: string;
  documentType?: string;
  fileType?: string;
  fileName: string;
}

interface FileChunk {
  partNumber: number;
  blob: Blob;
}

export interface DownloadResult {
  body: Blob;
}

interface DownloadState {
  chunks: Uint8Array[];
  mimeType: string;
}

type CompressionType = 'gzip' | 'zip' | 'zlib' | 'unknown' | 'none';

function detectCompression(bytes: Uint8Array): CompressionType {
  if (bytes.length < 2) { return 'none'; }

  // Check GZIP: 1F 8B
  if (bytes[0] === 0x1F && bytes[1] === 0x8B) {
    return 'gzip';
  }

  // Check Zlib (Deflate): 78 01, 78 9C, 78 DA
  if (bytes[0] === 0x78 && (bytes[1] === 0x01 || bytes[1] === 0x9C || bytes[1] === 0xDA)) {
    return 'zlib';
  }

  // Check ZIP: 50 4B 03 04 (Requires 4 bytes)
  if (bytes.length >= 4 &&
      bytes[0] === 0x50 && bytes[1] === 0x4B &&
      bytes[2] === 0x03 && bytes[3] === 0x04) {
    return 'zip';
  }

  return 'unknown';
}

export function downloadCompressedFileAsStream(
  stream$: Observable<FileChunkResponse>
): Observable<DownloadResult> {
  const initialState: DownloadState = {
    chunks: [],
    mimeType: 'image/jpeg' // Fallback to an image type since this is for thumbnails
  };

  let compressionFormat: CompressionType | null = null;

  return stream$.pipe(
    reduce((acc: DownloadState, chunk: FileChunkResponse) => {
      if (chunk.mimeType) {
        acc.mimeType = chunk.mimeType;
      }

      if (compressionFormat === null) {
        compressionFormat = detectCompression(chunk.content);
      }
      if (chunk.content) {
        const segment = chunk.content instanceof Uint8Array
          ? chunk.content
          : new Uint8Array(chunk.content);
        acc.chunks.push(segment);
      }

      return acc;
    }, initialState),
    switchMap((finalState) => {
      if (finalState.chunks.length === 0) {
        throw new Error('Stream completed empty.');
      }

      const blob = new Blob(finalState.chunks.map(ch=> ch.slice().buffer), { type: 'application/gzip' });

      if (compressionFormat !== null && (compressionFormat === 'gzip' || compressionFormat === 'zlib')) {
        const decompressionStream = new DecompressionStream(compressionFormat === 'gzip' ? compressionFormat : 'deflate');
        const decompressedReadableStream = blob.stream().pipeThrough(decompressionStream);
        const responsePromise = new Response(decompressedReadableStream).blob();
        return from(responsePromise).pipe(
          map((uncompressedBlob) =>
            ({
              body: new Blob([uncompressedBlob])
            })
          )
        );
      } else if (compressionFormat === 'unknown' || compressionFormat === 'none' || compressionFormat === 'zip') {
        return from([{
          body: blob
        }]);
      } else {
        throw new Error(`Compression ${compressionFormat} is not supported.`);
      }
    })
  );
}

@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  constructor(private _documentService: DocumentServiceSpa) { }

  download(fileId: string): Observable<DownloadResult> {
    return downloadCompressedFileAsStream(this._documentService.getFile({documentId: fileId, offset: 0}));
  }

  async getDownloadUrl(fileId: string): Promise<string> {
    return this._documentService.getFileUrl({ documentId: fileId }).then(r => r.url);
  }

  addChunks(docType: string, fileToUpload: any, fileName: string | null = null, contentType: string | null = null): Observable<DocumentReference> {
    const fileId = uuidv4();
    const chunkSize = 900000;
    const numberOfChunks = Math.ceil(fileToUpload.size / chunkSize);

    const chunks = [...Array(numberOfChunks).keys()].map(i => {
      const start = i * chunkSize;
      const chunkEnd = Math.min(start + chunkSize, fileToUpload.size);
      const chunk = fileToUpload.slice(start, chunkEnd);
      return {
        partNumber: i,
        blob: chunk,
      };
    });

    return from(this.uploadChunks(fileId, chunks, docType, fileName || '', contentType || '').then(d => ({
      id: d.id,
      fileId: d.fileId,
      documentType: d.documentType,
      fileType: d.fileType,
      fileName: d.fileName
    })));
  }

  private async uploadChunks(id: string, chunks: FileChunk[], docType: string, fileName: string, contentType: string): Promise<DocumentInternal> {
    await Promise.all(
      chunks.map(async (c) => {
        const arrayBuffer = await c.blob.arrayBuffer();
        const request = {
          id,
          partNumber: c.partNumber,
          body: new Uint8Array(arrayBuffer)
        };
        return this._documentService.uploadChunk(request);
      })
    );
    const request = {
      id,
      numberOfChunks: chunks.length,
      contentType,
      fileName: fileName,
      documentType: docType,
    };

    return this._documentService.verifyUploadedChunks(request);
  }

  async calculateFileSHA256(file: File) {
    const arrayBuffer = await file.arrayBuffer();

    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const base64Checksum = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));

    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return [hashHex, base64Checksum];
  }

  async putToPresignedUrl(url: string, base64Checksum: string, file: File) {
    const e = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'x-amz-checksum-sha256': base64Checksum,
        'x-amz-sdk-checksum-algorithm': 'SHA256',
        'content-type': file.type
      }}
    );
    if (!e.ok) {
      throw new Error(`Upload failed with status ${e.status}`);
    }
  }

  async upload(file: File): Promise<DocumentInternal> {
    const [sha256, base64Checksum] = await this.calculateFileSHA256(file);
    const id = uuidv4();
    const request = {
      documentId: id.toString(),
      sha256Hash: sha256,
      base64Sha256Hash: base64Checksum,
      contentType: file.type,
    };
    const res = await this._documentService.uploadFile(request);

    switch(res.documentOrUpload?.$case) {
    case 'document':
      return res.documentOrUpload.value;
    case 'uploadUrl':
      await this.putToPresignedUrl(res.documentOrUpload.value, base64Checksum, file);
      return this._documentService.verifyUploadFile({
        documentId: id,
        documentType: '',
        contentType: file.type,
        fileName: file.name,
        contentHash: sha256
      });
    }
    throw new Error('Should not reach here');
  }
}
